import {format} from 'date-fns';
import {Link} from 'react-router-dom';

import {Pagination} from '@/components';
import {useAppUsageHistory} from '@/hooks/useAppUsageHistory';
import type {AfriexQueryParams} from '@/types';
import {AttributionEventType} from '@/types/attribution';

import AppMetricsFilters from './AppMetricsFilters';

// Define card colors for each event type
const eventColors: Record<AttributionEventType, string> = {
  [AttributionEventType.INSTALL]: 'bg-blue-100 dark:bg-blue-900',
  [AttributionEventType.REINSTALL]: 'bg-purple-100 dark:bg-purple-900',
  [AttributionEventType.UNINSTALL]: 'bg-red-100 dark:bg-red-900',
  [AttributionEventType.FIRST_OPEN]: 'bg-green-100 dark:bg-green-900',
  [AttributionEventType.APP_REMOVE]: 'bg-yellow-100 dark:bg-yellow-900',
};

// An empty result is usually a half-filled date range rather than a genuine
// absence of events, so say which end is missing.
const resolveEmptyStateMessage = (
  fromDate: AfriexQueryParams['fromDate'],
  toDate: AfriexQueryParams['toDate'],
) => {
  if (fromDate && !toDate) {
    return 'Please select an end date';
  }
  if (!fromDate && toDate) {
    return 'Please select a start date';
  }
  return 'No data found';
};

export default function AppUsageHistoryTable() {
  const {
    data,
    isLoading,
    isSearch,
    isPageReset,
    pageInfo,
    fetchList,
    currentFilters,
    filtersToApply,
    handleApplyFilters,
    clearFilters,
    handleAutoComplete,
    selectAppliedFilters,
    selectCurrentFilters,
  } = useAppUsageHistory();

  return (
    <div className="space-y-4">
      <AppMetricsFilters
        currentFilters={currentFilters}
        filtersToApply={filtersToApply}
        clearFilters={clearFilters}
        selectAppliedFilters={selectAppliedFilters}
        selectCurrentFilters={selectCurrentFilters}
        handleApplyFilters={handleApplyFilters}
        handleAutoComplete={handleAutoComplete}
      />

      <section className="pt-5 md:pt-5">
        <div className="bg-white dark:bg-dark-inputBg rounded-t-lg shadow overflow-hidden border border-b-0 dark:border-dark-borderMid">
          {isLoading ? (
            <div className="p-8 flex justify-center">
              <div className="w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-borderHeavy">
                <thead className="bg-gray-50 dark:bg-dark-inputBg">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Event
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      User ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Platform
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      App Version
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Device ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Created At
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-dark-inputBg divide-y divide-gray-200 dark:divide-dark-borderHeavy">
                  {!data || data.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-6 py-4 text-center text-gray-500 dark:text-gray-400"
                      >
                        {resolveEmptyStateMessage(
                          filtersToApply.fromDate,
                          filtersToApply.toDate,
                        )}
                      </td>
                    </tr>
                  ) : (
                    data.map((event, index) => (
                      <tr
                        key={event.id}
                        className={`${
                          index % 2 === 0
                            ? ''
                            : 'bg-gray-50 dark:bg-dark-zebraRow'
                        } hover:bg-gray-100 dark:hover:bg-dark-borderHeavy transition-colors duration-150`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 capitalize">
                          <span
                            className={`inline-block px-2 py-1 rounded ${eventColors[event.event]} bg-opacity-50`}
                          >
                            {event.event.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {event.userId ? (
                            <Link
                              to={`/users/${event.userId}`}
                              className="text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                            >
                              {event.userId}
                            </Link>
                          ) : (
                            <span className="text-gray-500 dark:text-gray-400">
                              -
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 capitalize">
                          {event.platform}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {event.appVersion || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {event.idfa || event.idfv || event.deviceId || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {format(
                            new Date(event.createdAt),
                            'yyyy-MM-dd HH:mm:ss',
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {data && data.length > 0 && (
          <Pagination
            onPageChange={(queryParams) =>
              fetchList({
                page: queryParams.page,
                limit: queryParams.limit,
                events: currentFilters.events,
              })
            }
            total={pageInfo.total}
            isSearch={isSearch}
            isPageReset={isPageReset}
          />
        )}
      </section>
    </div>
  );
}
